/**
 * Excessive Access Dashboard Component
 * Main dashboard for displaying excessive access detection insights
 *
 * Features:
 * - Risk distribution cards (Critical, High, Moderate, Acceptable)
 * - Z-score heatmap visualization
 * - Peer cohort comparison charts
 * - Filterable user table with pagination and sorting
 * - Detailed user insight modal
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Alert,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  Button,
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import RefreshIcon from '@mui/icons-material/Refresh';
import { PageHeader } from '../../shared';
import apiService from '../services/apiService';
import RiskDistributionCard from './excessive-access/RiskDistributionCard';
import FilterBar from './excessive-access/FilterBar';
import ActiveFiltersBar from './excessive-access/ActiveFiltersBar';
import ZScoreHeatMap from './excessive-access/ZScoreHeatMap';
import CohortComparisonChart from './excessive-access/CohortComparisonChart';
import ExcessiveAccessUserTable from './excessive-access/ExcessiveAccessUserTable';
import UserDetailModal from './excessive-access/UserDetailModal';

const ExcessiveAccessDashboard = () => {
  const { getAccessTokenSilently, user: authUser } = useAuth0();

  // State for static data (loaded once)
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);
  const [cohortData, setCohortData] = useState([]);
  const [departments, setDepartments] = useState([]);

  // State for dynamic user data (changes with filters)
  const [usersLoading, setUsersLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);

  // Filter state
  const [filters, setFilters] = useState({
    department: 'all',
    classification: null,
    minRiskScore: null,
    maxRiskScore: null,
    minZScore: null,
    cohortId: null,
    sortBy: 'risk_score',
    sortOrder: 'desc',
    limit: 50,
    offset: 0
  });

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = filters.limit;

  // Modal state
  const [selectedUser, setSelectedUser] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Get tenant name from JWT token
  const getTenantName = useCallback(() => {
    if (authUser && authUser['https://trustx.cloud/claims/company']) {
      return authUser['https://trustx.cloud/claims/company'];
    }
    return null;
  }, [authUser]);

  // Initialize API service
  useEffect(() => {
    const initApiService = async () => {
      try {
        const token = await getAccessTokenSilently();
        const tenantName = getTenantName();

        apiService.setAuthToken(token);
        apiService.setTenant(tenantName);
        apiService.refreshConfig();
      } catch (err) {
        console.error('Failed to initialize API service:', err);
        setError('Failed to authenticate. Please try logging in again.');
      }
    };

    initApiService();
  }, [getAccessTokenSilently, authUser, getTenantName]);

  // Define fetch functions first (before useEffect hooks that call them)
  const fetchStaticDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch all static data in parallel
      const [summaryRes, heatmapRes, cohortRes] = await Promise.all([
        apiService.getExcessiveAccessSummary(),
        apiService.getExcessiveAccessZScoreHeatmap(),
        apiService.getExcessiveAccessCohortComparison()
      ]);

      if (!summaryRes.success) {
        throw new Error(summaryRes.error || 'Failed to fetch summary data');
      }
      if (!heatmapRes.success) {
        throw new Error(heatmapRes.error || 'Failed to fetch heatmap data');
      }
      if (!cohortRes.success) {
        throw new Error(cohortRes.error || 'Failed to fetch cohort data');
      }

      setSummaryData(summaryRes.data.data);
      setHeatmapData(heatmapRes.data.data.heatmap_data || []);
      setCohortData(cohortRes.data.data.cohorts || []);

      // Extract unique departments from heatmap data
      if (heatmapRes.data.data.heatmap_data) {
        const uniqueDepts = [...new Set(
          heatmapRes.data.data.heatmap_data
            .map(item => item.department)
            .filter(dept => dept && dept !== 'Unknown')
        )].sort();
        setDepartments(uniqueDepts);
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps - only uses stable apiService and setters

  const fetchUsersData = useCallback(async () => {
    setUsersLoading(true);

    try {
      // Build params for user query
      const params = {
        ...filters,
        department: filters.department === 'all' ? null : filters.department
      };

      const response = await apiService.getExcessiveAccessInsights(params);

      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch users');
      }

      setUsers(response.data.data.insights || []);
      setTotalUsers(response.data.data.total || 0);

    } catch (err) {
      console.error('Error fetching users:', err);
      setError(err.message);
      setUsers([]);
      setTotalUsers(0);
    } finally {
      setUsersLoading(false);
    }
  }, [filters]); // Depends on filters state

  // Fetch static dashboard data (once on mount)
  useEffect(() => {
    fetchStaticDashboardData();
  }, [fetchStaticDashboardData]);

  // Fetch user data when filters change
  useEffect(() => {
    fetchUsersData();
  }, [fetchUsersData]);

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      offset: 0 // Reset to first page when filters change
    }));
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({
      department: 'all',
      classification: null,
      minRiskScore: null,
      maxRiskScore: null,
      minZScore: null,
      cohortId: null,
      sortBy: 'risk_score',
      sortOrder: 'desc',
      limit: 50,
      offset: 0
    });
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterKey) => {
    if (filterKey === 'department') {
      handleFilterChange({ department: 'all' });
    } else {
      handleFilterChange({ [filterKey]: null });
    }
  };

  const handleClassificationClick = (classification) => {
    // If clicking the same classification, clear the filter
    if (filters.classification === classification) {
      handleFilterChange({ classification: null });
    } else {
      handleFilterChange({ classification });
    }
  };

  const handleSort = (field) => {
    const newSortOrder =
      filters.sortBy === field && filters.sortOrder === 'desc' ? 'asc' : 'desc';

    handleFilterChange({
      sortBy: field,
      sortOrder: newSortOrder
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * pageSize
    }));
  };

  const handleViewDetails = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleRefresh = () => {
    fetchStaticDashboardData();
    fetchUsersData();
  };

  if (loading) {
    return (
      <Box sx={{ py: 5, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          Loading Excessive Access Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error && !summaryData) {
    return (
      <Box sx={{ py: 5 }}>
        <Alert severity="error">
          <Typography variant="subtitle2" fontWeight={700}>
            Error Loading Dashboard
          </Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <PageHeader
        title="Excessive Access Detection"
        description="AI-powered detection of users with access significantly above their peer cohort"
        pillarId="risk"
        actions={
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleRefresh} disabled={loading || usersLoading} size="small">Refresh</Button>
        }
      />

      {error && (
        <Alert severity="warning" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Risk Distribution Cards */}
      <Box sx={{ mb: 2 }}>
        <RiskDistributionCard
          summaryData={summaryData}
          onCardClick={handleClassificationClick}
          selectedClassification={filters.classification}
        />
      </Box>

      {/* Visualizations */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: { xs: 'wrap', lg: 'nowrap' } }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <ZScoreHeatMap heatmapData={heatmapData} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CohortComparisonChart cohortData={cohortData} />
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ mb: 2 }}>
        <FilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          departments={departments}
        />
      </Box>

      {/* Active Filters */}
      <ActiveFiltersBar
        filters={filters}
        onRemoveFilter={handleRemoveFilter}
      />

      {/* User Table */}
      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
            Users with Excessive Access
          </Typography>
          <ExcessiveAccessUserTable
            users={users}
            loading={usersLoading}
            sortBy={filters.sortBy}
            sortOrder={filters.sortOrder}
            currentPage={currentPage}
            totalUsers={totalUsers}
            pageSize={pageSize}
            onSort={handleSort}
            onPageChange={handlePageChange}
            onViewDetails={handleViewDetails}
          />
        </CardContent>
      </Card>

      {/* User Detail Modal */}
      <UserDetailModal
        show={showModal}
        onHide={() => setShowModal(false)}
        user={selectedUser}
      />
    </Box>
  );
};

export default ExcessiveAccessDashboard;
